import { Award, Users, Clock, MapPin } from 'lucide-react'

const features = [
  {
    icon: Award,
    title: 'Certifierade frisörer',
    description: 'Vårt team består av utbildade och erfarna frisörer med branschkännedom.',
  },
  {
    icon: Users,
    title: 'Personlig service',
    description: 'Vi tar tid för varje kund och skapar skräddarsydda lösningar efter dina önskemål.',
  },
  {
    icon: Clock,
    title: 'Flexibel bokning',
    description: 'Boka enkelt online när det passar dig, även med kort varsel när det finns tider.',
  },
  {
    icon: MapPin,
    title: 'Centralt läge',
    description: 'Vår salong ligger på Säbygatan 15 i Landskrona med goda kommunikationer.',
  },
]

export default function About() {
  return (
    <section id="om-oss" className="relative py-24 lg:py-32 bg-dark-800 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 xl:px-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <div className="relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl shadow-gold-500/10">
              {/* Client Transformation Images - Före & Efter */}
              <div className="flex w-full h-full">
                {/* Före Bild */}
                <div className="relative w-1/2 h-full">
                  <img
                    src="/fore.jpg"
                    alt="Före klippning"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-dark-900/80 backdrop-blur-sm rounded-full border border-white/10">
                    <span className="text-xs font-medium text-white tracking-wide uppercase">Före</span>
                  </div>
                </div>
                {/* Efter Bild */}
                <div className="relative w-1/2 h-full">
                  <img
                    src="/efter.jpg"
                    alt="Efter klippning"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-gold-500/90 backdrop-blur-sm rounded-full">
                    <span className="text-xs font-bold text-dark-900 tracking-wide uppercase">Efter</span>
                  </div>
                </div>
                {/* Divider Line */}
                <div className="absolute inset-y-0 left-1/2 w-px bg-gradient-to-b from-transparent via-white/30 to-transparent" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900/40 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Floating Card - Recension */}
            <div className="absolute -bottom-6 -right-6 lg:-right-12 p-6 rounded-2xl glass max-w-xs">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center overflow-hidden">
                  {/* Logo image - lägg till logo.jpg i public-mappen */}
                  <img
                    src="/logo.jpg"
                    alt="VK Studio"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      (e.currentTarget.parentElement?.querySelector('.fallback-text') as HTMLElement).style.display = 'flex';
                    }}
                  />
                  <div className="fallback-text w-full h-full items-center justify-center text-dark-900 font-bold text-xl" style={{ display: 'none' }}>
                    VK
                  </div>
                </div>
                <div>
                  <div className="font-display font-semibold text-white">VK Studio</div>
                  <div className="text-sm text-gray-400">Etablerad 2009</div>
                </div>
              </div>

              <p className="text-sm text-gray-300 italic">
                "Skönhet är inte bara utseende – det är känslan av att vara sitt bästa jag."
              </p>
            </div>

          </div>

          {/* Content Side */}
          <div>
            <span className="inline-block text-gold-400 font-semibold text-sm tracking-wider uppercase mb-4">
              Om Oss
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mb-6">
              En plats där <span className="text-gradient">kreativitet</span> möter hantverk
            </h2>
            <div className="space-y-4 text-gray-300 leading-relaxed mb-8">
              <p>
                VK Studio har varit en del av Landskronas frisörscen sedan 2009. 
                Det som började som en liten dröm har vuxit till en välkänd salong där 
                kunder kommer tillbaka gång efter gång.
              </p>
              <p>
                Vi tror på att varje person är unik, och därför tar vi alltid tid för 
                en grundlig konsultation innan vi börjar. Vårt mål är inte bara att ge 
                dig en fin frisyr – utan att skapa något som verkligen passar just dig, 
                din livsstil och din personlighet.
              </p>
              <p>
                Med en passion för hår och en ödmjuk inställning till branschen, 
                strävar vi alltid efter att ligga i framkant när det gäller tekniker 
                och trender, samtidigt som vi aldrig glömmer vikten av klassisk hantverkskunnighet.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((feature) => (
                <div key={feature.title} className="flex gap-4 p-4 rounded-xl bg-dark-700/50 hover:bg-dark-700 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-gold-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                    <p className="text-sm text-gray-400">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
